<template>
  <Layout class="billing-layout">
    <HeaderRoot title="Billing" />
    <div v-if="isLoadingBilling()" class="layout__wrapper">
      <section class="subscription-box">
        <loading-spinner />
      </section>
    </div>
    <div v-else-if="isErrorLoadingBilling()" class="layout__wrapper">
      <section class="subscription-box">
        <LoadingError />
      </section>
    </div>
    <div v-else-if="isAppleWebBilling" class="layout__wrapper container">
      <ContentWrapper>
        You are subscribed with Apple in-app purchase subscription. Please use
        native iOS app or
        <a
          :href="billing.paymentProviderSettingsUrl"
          target="_blank"
          class="provider-settings-link"
        >
          Apple subscription interface
        </a>
        to manage your subscription.
      </ContentWrapper>
    </div>
    <div v-else-if="isGoogleWebBilling" class="layout__wrapper container">
      <ContentWrapper>
        You are subscribed through Google Play. Please use the native Android
        app or the
        <a
          :href="billing.paymentProviderSettingsUrl"
          target="_blank"
          class="provider-settings-link"
        >
          Google Play settings
        </a>
        to manage your subscription.
      </ContentWrapper>
    </div>
    <BillingData v-else-if="isFree" :billing="billing" />
    <BillingDataPaid v-else-if="isStripePaid" :billing="billing" />
    <BillingDataPaidAppStore v-else-if="isApplePaid" :billing="billing" />
  </Layout>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref, watch } from 'vue'
import { useWait } from '@/app/composables/useWait'
import { useRouter } from 'vue-router'

import Layout from '@/app/components/Layout.vue' // @ is an alias to /src
import HeaderRoot from '@/app/components/header/Root.vue'
import BillingData from '@/app/components/billing/BillingData.vue'
import BillingDataPaid from '@/app/components/billing/BillingDataPaid.vue'
import BillingDataPaidAppStore from '@/app/components/billing/BillingDataPaidAppStore.vue'
import LoadingSpinner from '@/components/ui/LoadingSpinner.vue'
import ContentWrapper from '@/components/ui/ContentWrapper.vue'

import { assertNotNull } from '@/helpers/typing'
import { backend } from '@/services/backend'
import { Sentry } from '@/services/sentry'
import {
  BillingView,
  createBilling,
  isAppleWeb,
  isGoogleWeb,
} from '@/models/billing'
import { iosExternalLinkPayment16218Variant } from '@/services/ab'

const router = useRouter()
const wait = useWait()

const billing = ref<BillingView | null>(null)
const redirectDelay = ref(6000)

const isAppleWebBilling = ref(false)
const isGoogleWebBilling = ref(false)

onBeforeMount(async () => {
  wait.start('Loading Billing')
  await refreshBilling()
})

async function refreshBilling(): Promise<void> {
  wait.start('Loading Billing')
  wait.end('Error Loading Billing')
  try {
    const response = await backend.getBilling()
    billing.value = await createBilling(response)
    isAppleWebBilling.value = await isAppleWeb(response)
    isGoogleWebBilling.value = await isGoogleWeb(response)
  } catch (error) {
    wait.start('Error Loading Billing')
    // The error here is usually NetworkError or Timeout, but
    // we still capture all errors to Sentry to be aware if
    // error rate increases.
    Sentry.withScope(function (scope) {
      scope.setFingerprint(['billing-loading-error'])
      Sentry.captureException(error)
    })
  } finally {
    wait.end('Loading Billing')
  }
}

/**
 * Handles the redirect after subscription has been made.
 */
watch(
  () => billing.value?.subscribedAlertAndRedirect,
  async (value?: boolean /* , old: boolean */) => {
    if (value !== true) {
      return
    }

    // Redirect to 'books' page after delay when switching to the paid plan.
    setTimeout(() => {
      const currentRoute = router.currentRoute.value
      if (currentRoute.name !== 'billing') {
        // The user has already navigated out of the billing page,
        // skip the redirect.
        return
      }
      router.push({ name: 'books' })
    }, redirectDelay.value)
  },
)

function isLoadingBilling(): boolean {
  return wait.is('Loading Billing')
}

function isErrorLoadingBilling(): boolean {
  return wait.is('Error Loading Billing')
}

const isFree = computed(() => {
  const billingValue = assertNotNull(billing.value)
  return !billingValue.isPaidPlan
})

const isStripePaid = computed(() => {
  const billingValue = assertNotNull(billing.value)

  const isAppleExternalBilling16218 =
    billingValue.billing_type === 'stripe' &&
    iosExternalLinkPayment16218Variant() === 'B'

  return (
    billingValue.isPaidPlan &&
    (!billingValue.isAppStore() || isAppleExternalBilling16218)
  )
})

const isApplePaid = computed(() => {
  const billingValue = assertNotNull(billing.value)
  return billingValue.isPaidPlan && billingValue.isAppStore()
})
</script>

<style scoped lang="scss">
.provider-settings-link {
  text-decoration: underline;
}

.content-wrapper-block {
  margin-top: var(--container-padding);
}
</style>
